// Player Bio widget (v2) — the prototype's Bio card on live Catalyst data. Shows
// one player: name, position, the meta rows, and the HOPS rating bar. Single
// player (the shell's header search sets it); not a merge/drop target.
//
// Two fields aren't on /player, so we resolve them from the player's scope
// (Prateek's call — include them now, don't defer):
//   • Competition NAME  ← the player's competition list, matched to the ref's comp
//   • Position          ← the player's most-played position (a /player-stats call),
//                          named via /positions
// Both degrade to "—" while loading or when genuinely absent (no fabricated data).

import { useMemo } from "react";
import type { WidgetProps } from "../registry.js";
import {
  usePlayerBio,
  usePlayerCompetitions,
  usePlayerPositionMinutes,
} from "../../data/players.js";
import { usePositions } from "../../data/stats.js";
import { theme } from "../theme.js";

const DASH = "—";

function calcAge(dob: string | null): number | null {
  if (!dob) return null;
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age -= 1;
  return age;
}

function formatDate(iso: string | null): string {
  if (!iso) return DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

// Prototype .bio-meta-row: t-body, a border-subtle divider under each row (none on
// the last), label = text-muted, value = text-secondary weight 500.
function MetaRow({ label, value, last }: { label: string; value: string; last?: boolean }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 8,
        fontSize: theme.fontSize.body,
        paddingBottom: last ? 0 : 6,
        borderBottom: last ? "none" : `1px solid ${theme.borderSubtle}`,
      }}
    >
      <span style={{ color: theme.inkFaint }}>{label}</span>
      <span
        style={{
          color: theme.inkMuted,
          fontWeight: 500,
          textAlign: "right",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {value}
      </span>
    </div>
  );
}

export default function BioWidget({ players, onPlayerDragStart }: WidgetProps) {
  const ref = players[0] ?? null;
  const playerId = ref?.playerId ?? null;
  const competitionId = ref?.competitionId ?? null;
  const seasonId = ref?.seasonId ?? null;

  const bioQ = usePlayerBio(playerId);
  const compsQ = usePlayerCompetitions(playerId);
  const posMinutesQ = usePlayerPositionMinutes(playerId, competitionId, seasonId);
  const positionsQ = usePositions();

  const competitionName = useMemo(() => {
    if (competitionId === null) return DASH;
    const found = compsQ.data?.competitions.find((c) => c.id === competitionId);
    return found?.name ?? DASH;
  }, [compsQ.data, competitionId]);

  // Most-played position code (top minutes) → readable name via /positions.
  const positionName = useMemo(() => {
    const rows = posMinutesQ.data?.rows;
    if (!rows || rows.length === 0) return DASH;
    const top = [...rows].sort((a, b) => b.minutes - a.minutes)[0];
    if (!top) return DASH;
    const named = positionsQ.data?.positions.find((p) => p.id === top.position_id);
    return named?.name ?? top.position_id ?? DASH;
  }, [posMinutesQ.data, positionsQ.data]);

  if (!ref) {
    return <div style={emptyStyle}>Search a player to begin.</div>;
  }
  if (bioQ.isLoading) {
    return <div style={emptyStyle}>Loading…</div>;
  }
  if (bioQ.isError || !bioQ.data) {
    return <div style={{ ...emptyStyle, color: theme.danger }}>Couldn’t load this player.</div>;
  }

  const p = bioQ.data.player;
  const age = calcAge(p.dob);
  const rating = p.hops?.rating ?? null;
  const lastAppearance = p.mostRecentMatchDate
    ? `${formatDate(p.mostRecentMatchDate)}${p.mostRecentTeam ? ` · ${p.mostRecentTeam}` : ""}`
    : DASH;

  const metaRows: Array<[string, string]> = [
    ["Team", p.mostRecentTeam ?? DASH],
    ["Competition", competitionName],
    ["Country", p.country ?? DASH],
    ["Age", age !== null ? `${age} · ${formatDate(p.dob)}` : DASH],
    ["Height", p.height ? `${p.height} cm` : DASH],
    ["Weight", p.weight ? `${p.weight} kg` : DASH],
    ["Last Appearance", lastAppearance],
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, height: "100%" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Name = the prototype's .bio-name: Instrument Sans (NOT Barlow),
            t-heading, weight 600. It's a drag source (onto a stats card to
            compare, or empty canvas to spawn another Bio). */}
        <div
          onPointerDown={onPlayerDragStart ? (e) => onPlayerDragStart(e, ref) : undefined}
          title="Drag onto a stats card to compare"
          style={{
            color: theme.ink,
            fontFamily: theme.fontBody,
            fontSize: theme.fontSize.heading,
            fontWeight: 600,
            lineHeight: 1.15,
            cursor: onPlayerDragStart ? "grab" : "default",
            userSelect: "none",
            touchAction: "none",
            display: "inline-block",
            alignSelf: "flex-start",
          }}
        >
          {p.name}
        </div>
        {p.nickname && p.nickname !== p.name ? (
          <div style={{ color: theme.inkFaint, fontSize: theme.fontSize.data }}>{p.nickname}</div>
        ) : null}
        {/* Prototype .bio-position: t-label, uppercase, green outline pill. */}
        <span
          style={{
            alignSelf: "flex-start",
            marginTop: 6,
            padding: "3px 8px",
            borderRadius: 4,
            background: "rgba(46, 204, 113, 0.12)",
            border: "1px solid rgba(46, 204, 113, 0.3)",
            color: theme.accent,
            fontSize: theme.fontSize.label,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          {positionName}
        </span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {metaRows.map(([label, value], i) => (
          <MetaRow key={label} label={label} value={value} last={i === metaRows.length - 1} />
        ))}
      </div>

      <div style={{ marginTop: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
          <span style={{ color: theme.inkFaint, fontSize: theme.fontSize.body }}>HOPS Rating</span>
          <span
            style={{
              color: theme.ink,
              fontFamily: theme.fontBody,
              fontSize: theme.fontSize.title,
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {rating !== null ? (rating * 100).toFixed(1) : DASH}
          </span>
        </div>
        <div style={{ height: 8, borderRadius: 4, background: theme.field, overflow: "hidden" }}>
          <div
            style={{
              height: "100%",
              width: `${rating !== null ? Math.max(2, rating * 100) : 0}%`,
              background: "linear-gradient(90deg,#34d399,#10b981)",
              borderRadius: 4,
              transition: "width 450ms cubic-bezier(0.22,1,0.36,1)",
            }}
          />
        </div>
      </div>
    </div>
  );
}

const emptyStyle: React.CSSProperties = {
  display: "grid",
  placeItems: "center",
  height: "100%",
  color: theme.inkMuted,
  fontSize: 13,
  textAlign: "center",
  padding: 12,
};
